import PerformanceHeader from "../Performance/PerformanceHeader";
import CommercialCardDesktop from "../CommercialCardDesktop";
import styled from "styled-components";
import { CommercialUnitData } from "../../types/commercial";
import { useTranslation } from "react-i18next";
import { useKpiVisibility } from "../../kpis/useKpiVisibility";
import CaCard from "../CaCard";
import { ContentsProps } from "../CommercialContentPage/types";
import { useCommercialContentPageMetrics } from "../CommercialContentPage/useCommercialContentPageMetrics";
import TurnoverTriptychCardDesktop from "../TurnoverTriptychCardDesktop";
import { InfoBullType } from "../../types/infoBull";
import { DriveTurnover } from "../../types/drive";
import PerformanceContent from "../Performance/PerformanceContent/PerformanceContent";
import { Range } from "../../utils/range";
import { PerformanceProvider } from "../Performance/context/PerformanceContext";

const PERFORMANCE_CARD_HEIGHT = 166;
const PERFORMANCE_GAP = 16;
const TRIPTYCH_HEIGHT = PERFORMANCE_CARD_HEIGHT * 2 + PERFORMANCE_GAP;

const CommercialContentPageDesktop = ({
                                        //global
                                        range,
                                        //turnover data
                                        currentDriveData,
                                        currentTurnoverData,
                                        currentTurnoverPromoData,
                                        currentMemberCardTurnoverData,
                                        pastTurnoverPromoData,
                                        pastMemberCardTurnoverData,
                                        driveObjective,
                                        isLoadingTurnover,
                                        hasErrorTurnover,
                                        openInformationModal,
                                        pastDriveData,
                                        pastTurnoverData,
                                        turnoverObjectiveData,
                                        // tryptich turnover
                                        currentTurnoverTriptychData,
                                        pastTurnoverTriptychData
                                      }: ContentsProps) => {

  const {
    basketRHRate,
    ticketsRHRate,
    promoRHRate,
    memberCardRHRate,
    turnoverSdvRHRate,
    driveRHRate,
    triptychTurnoverRates,
    formatNumber,
    ratioToPercentage,
  } = useCommercialContentPageMetrics({
    currentTurnoverData,
    pastTurnoverData,
    currentTurnoverPromoData,
    currentMemberCardTurnoverData,
    pastTurnoverPromoData,
    pastMemberCardTurnoverData,
    currentDriveData,
    pastDriveData,
    currentTurnoverTriptychData,
    pastTurnoverTriptychData,
    strictRatioRangeValidation: true,
  });

  const {t} = useTranslation();

  const {isKpiExists} = useKpiVisibility();
  const hasTriptych = isKpiExists("Commercial", "TurnoverTriptych") && currentTurnoverTriptychData?.some(item => item.turnover !== null && item.turnover !== undefined);


    const buildInfoBull = (title: string, data: any): InfoBullType => {
    return {
      lastUpdated: data?.importedDateTime,
      description: title,
      loading: isLoadingTurnover,
      isUnavailable: hasErrorTurnover || data?.turnover === null,
      error: hasErrorTurnover || data?.turnover === null
    };
  };

  return (
      <>
        <Container $hasTriptych={hasTriptych}>
          <LeftColumn $isDirect={range === Range.Direct} $hasTriptych={hasTriptych}>
            { isKpiExists("Commercial", "Turnover") && (
                <>
                  {/* CA SDV */}
                  <CaCard
                      title={t('commercial.sdv')}
                      target={turnoverObjectiveData?.objectiveAmount}
                      value={currentTurnoverData?.turnover}
                      rate={turnoverSdvRHRate as number}
                      infoBull={buildInfoBull(t('commercial.sdv'), currentTurnoverData as CommercialUnitData)}
                  />
                  {/* CA Drive */}
                  <CaCard
                      title={t('commercial.ca_ecommerce')}
                      target={driveObjective?.objectiveAmount}
                      value={currentDriveData?.turnover}
                      rate={driveRHRate as number}
                      infoBull={buildInfoBull(t('commercial.ca_ecommerce'), currentDriveData as DriveTurnover)}
                  />
                </>
            )}

            { isKpiExists("Commercial", "AverageBasket") && (
                <CommercialCardDesktop
                    title={t('commercial.panier_moyen')}
                    subTitle={t('commercial.hors_ecommerce')}
                    turnover={currentTurnoverData?.averageBasket}
                    rhRate={basketRHRate as number}
                    isLoading={isLoadingTurnover}
                    currency={"€"}
                    isUnavailable={hasErrorTurnover || currentTurnoverData?.averageBasket === null}
                    importedDateTime={currentTurnoverData?.importedDateTime}
                    openInformationModal={() => null}
                />
            )}
            { isKpiExists("Commercial", "TicketsCount") && (
                <CommercialCardDesktop
                    title={t('commercial.debits')}
                    subTitle={t('commercial.hors_ecommerce')}
                    turnover={currentTurnoverData?.ticketsCount}
                    rhRate={ticketsRHRate as number}
                    isLoading={isLoadingTurnover}
                    isUnavailable={hasErrorTurnover || currentTurnoverData?.ticketsCount === null}
                    currency={"k"}
                    importedDateTime={currentTurnoverData?.importedDateTime}
                    openInformationModal={() => null}
                />
            )}
            { !currentMemberCardTurnoverData?.disabled && isKpiExists("Commercial", "MemberCardTurnover") && (
                <CommercialCardDesktop
                    title={t('commercial.poids_encarté')}
                    subTitle={t('commercial.hors_ecommerce')}
                    turnover={ratioToPercentage(currentMemberCardTurnoverData?.turnoverRate)}
                    turnoverTotal={formatNumber(currentMemberCardTurnoverData?.turnover)}
                    rhRate={memberCardRHRate as number}
                    rhRateCurrency={' pt'}
                    currency={"%"}
                    isLoading={isLoadingTurnover}
                    isUnavailable={hasErrorTurnover || currentMemberCardTurnoverData?.turnover === null}
                    importedDateTime={currentMemberCardTurnoverData?.importedDateTime}
                    openInformationModal={() => null}
                />
            )}
            { !currentTurnoverPromoData?.disabled && isKpiExists("Commercial", "TurnoverPromo") && (
                <CommercialCardDesktop
                    title={t('commercial.poids_promo')}
                    subTitle={t('commercial.hors_ecommerce')}
                    turnover={ratioToPercentage(currentTurnoverPromoData?.turnoverRate)}
                    turnoverTotal={formatNumber(currentTurnoverPromoData?.turnover)}
                    rhRate={promoRHRate as number}
                    rhRateCurrency={' pt'}
                    currency={"%"}
                    isLoading={isLoadingTurnover}
                    isUnavailable={hasErrorTurnover || currentTurnoverPromoData?.turnover === null}
                    importedDateTime={currentTurnoverPromoData?.importedDateTime}
                    openInformationModal={() => null}
                />
            )}
          </LeftColumn>
          {hasTriptych && (
            <RightColumn>
              <TurnoverTriptychCardDesktop
                  title={t('commercial.chiffre_affaires_par_triptych')}
                  isLoading={isLoadingTurnover}
                  isUnavailable={!hasTriptych}
                  triptychTurnoverRates={triptychTurnoverRates()}
                  openInformationModal={() => null}
              />
            </RightColumn>
          )}
        </Container>
          <PerformanceProvider>
            <PerformanceCard>
            <PerformanceHeader />
              <PerformanceContent importedDateTime={currentTurnoverData?.importedDateTime} />
            </PerformanceCard>
          </PerformanceProvider>
      </>
  );
};

export default CommercialContentPageDesktop;

const Container = styled.div<{ $hasTriptych?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  align-items: stretch;
  width: 100%;
  gap: 16px;

  ${({ $hasTriptych }) => $hasTriptych && `
    @media only screen and (min-width: 1300px) {
      grid-template-columns: 3fr 1fr;
    }
  `}

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div<{ $isDirect: boolean; $hasTriptych?: boolean }>`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  align-items: stretch;
  gap: 16px;
  height: 100%;

  @media only screen and (min-width: 768px) {
    grid-template-columns: ${({ $isDirect }) =>
      $isDirect ? "repeat(3, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))"};
  }

  @media only screen and (min-width: 1024px) {
    grid-template-columns: ${({ $isDirect }) =>
      $isDirect ? "repeat(4, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))"};
  }

  > * {
    width: 100%;
    height: 100%;
  }

  @media only screen and (min-width: 1300px) {
    grid-auto-rows: ${PERFORMANCE_CARD_HEIGHT}px;

    > * {
      height: ${PERFORMANCE_CARD_HEIGHT}px;
    }
  }

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    gap: 8px;
    grid-auto-rows: 1fr;
    grid-template-columns: repeat(2, minmax(0, 1fr));

    ${({ $isDirect }) =>
      !$isDirect &&
      `
      @media (min-width: 668px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
    `}

    > * {
      height: 100%;
    }
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  > * {
    flex: 1;
    height: 100%;
  }

  @media only screen and (min-width: 1300px) {
    height: ${TRIPTYCH_HEIGHT}px;

    > * {
      height: ${TRIPTYCH_HEIGHT}px;
    }
  }

  @media only screen and (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
    height: auto;

    > * {
      height: auto;
    }
  }
`;

const PerformanceCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
